import {
  selectFilteredFeatures,
  selectSelectedTrialContractInfo,
} from "../selectors/resultMap";
import BulkExportDrawer from "../clientDashboard/bulkExportDrawer";
import { hasFeaturesRole } from "../users/rolesUtil";
import { useSelector } from "react-redux";
import { useState } from "react";

const plotIdsOf = (features) => [
  ...new Set(features.map((f) => f.properties?.plot_id ?? f.id)),
];

// Bulk export from the map and the analytics dashboards (SiteDrawer,
// ControlSection): each draws its own trigger, the drawer and its
// filter context live here. `children` are extra exports drawn in the drawer.
export const useMapBulkExport = (children = null) => {
  const [trial, selectedDate, filteringProfile, features] = useSelector(
    ({ resultMap }) => [
      resultMap.trial,
      resultMap.trial_date,
      resultMap.filteringProfile,
      resultMap.features,
    ]
  );
  const user = useSelector(({ user }) => user);
  const filteredFeatures = useSelector(selectFilteredFeatures);
  const contractInfo = useSelector(selectSelectedTrialContractInfo);

  // The filter context, captured when the drawer opens: the drawer reads its
  // dates at mount, so a context following the map (another trial, or none)
  // would export one site with another site's dates. The campaign tree is
  // captured with it for the same reason: a live one could drop the
  // preselected site out of the tree while the drawer is open.
  const [opened, setOpened] = useState(null);

  const open = () => {
    const plotIds = plotIdsOf(filteredFeatures);
    // The tree targets the campaign from `contracts`: no contractInfoId here.
    const filterContext = {
      // display_name: named in the note on when the profile applies.
      site: { id: trial.id, display_name: trial.display_name },
      // Default selection (the date shown on the map) + every date of the
      // site, so the drawer can offer a multi-date picker.
      dates: selectedDate ? [selectedDate] : [],
      availableDates: [...(trial.trial_dates || [])].map(String).sort(),
      // The plots shown on the map: the map selectors already applied the
      // profile, the scope filters, the layer and the excluded groups, so none
      // of that logic is duplicated server-side.
      plotIds,
      // Any of those filters counts, not only a saved profile: filtering two
      // experiments without saving a scope also hides plots.
      filterActive: plotIds.length < plotIdsOf(features).length,
      // Name of the applied profile, when it is a saved/named one (ad-hoc
      // filters have no name), shown next to the "With profiles" option.
      profileName: filteringProfile?.name || null,
    };
    // canExport guarantees contractInfo is loaded.
    setOpened({
      filterContext,
      contracts: [contractInfo],
      trials: user.trials,
    });
  };

  return {
    // view_features is what the backend checks on the campaign.
    canExport: Boolean(trial) && hasFeaturesRole(contractInfo?.roles),
    open,
    drawer: opened && (
      <BulkExportDrawer
        isOpen
        onClose={() => setOpened(null)}
        user={user}
        // The site's campaign as a tree, so its other sites can be exported too.
        contracts={opened.contracts}
        trials={opened.trials}
        filterContext={opened.filterContext}
      >
        {children}
      </BulkExportDrawer>
    ),
  };
};
